import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
    Box, Typography, CircularProgress, MenuItem, TextField, Paper, Stack, InputAdornment,
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Chip, Divider, Alert
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import ExcelJS from "exceljs";
import AppHeader from "../../components/AppHeader";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const LeaveBalances = ({ setIsLoggedIn }) => {
    const [rowData, setRowData] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split("T")[0]);

    // Filters
    const [selectedBranch, setSelectedBranch] = useState("");
    const [searchText, setSearchText] = useState("");

    const tenant = localStorage.getItem("tenant") || "";
    const [leaveTypes, setLeaveTypes] = useState([]);

    // Audit Dialog State
    const [openAuditDialog, setOpenAuditDialog] = useState(false);
    const [selectedAudit, setSelectedAudit] = useState(null);
    const [auditData, setAuditData] = useState(null);
    const [auditLoading, setAuditLoading] = useState(false);

    useEffect(() => {
        if (openAuditDialog && selectedAudit) {
            const fetchAudit = async () => {
                setAuditLoading(true);
                try {
                    const res = await axios.get(
                        `${process.env.REACT_APP_API_URL1}/leave/admin/accrual-audit?staff_id=${selectedAudit.staff_id}&leave_type_id=${selectedAudit.leave_type_id}&year=${selectedAudit.year}`,
                        {
                            withCredentials: true,
                            headers: { "X-Tenant": tenant }
                        }
                    );
                    if (res.data.success) {
                        setAuditData(res.data.data);
                    }
                } catch (err) {
                    console.error("Accrual audit fetch error:", err);
                }
                setAuditLoading(false);
            };
            fetchAudit();
        } else {
            setAuditData(null);
        }
    }, [openAuditDialog, selectedAudit, tenant]);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/leave/admin/balances?asOfDate=${asOfDate}`, {
                withCredentials: true,
                headers: { "X-Tenant": tenant }
            });

            if (res.data.success) {
                const { data, leaveTypes } = res.data;
                setLeaveTypes(leaveTypes || []);

                // Build dynamic columns
                const baseCols = [
                    {
                        headerName: "No.",
                        valueGetter: "node.rowIndex + 1",
                        pinned: "left",
                        width: 60,
                        sortable: false,
                        filter: false
                    },
                    { field: "staff_code", headerName: "Staff Code", pinned: "left", width: 120 },
                    { field: "full_name", headerName: "Name", pinned: "left", width: 200 },
                    { field: "branch", headerName: "Branch/Dept", width: 150 },
                ];

                const dynamicCols = leaveTypes.map(lt => ({
                    headerName: lt.type_name,
                    children: [
                        {
                            field: `${lt.type_code}_balance`,
                            headerName: "Bal",
                            width: 80,
                            cellStyle: { cursor: 'pointer', color: '#1a237e', textDecoration: 'underline', fontWeight: 'bold' }
                        },
                        { field: `${lt.type_code}_used`, headerName: "Used", width: 80 }
                    ]
                }));

                const processedData = (data || []).map(row => {
                    const newRow = { ...row };
                    leaveTypes.forEach(lt => {
                        const balKey = `${lt.type_code}_balance`;
                        if (newRow[balKey] != null) {
                            newRow[balKey] = Math.floor(Number(newRow[balKey]) * 2) / 2;
                        }
                    });
                    return newRow;
                });

                setColumnDefs([...baseCols, ...dynamicCols]);
                setRowData(processedData);
            }
        } catch (err) {
            console.error("Load balances error:", err);
        }
        setLoading(false);
    }, [tenant, asOfDate]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const uniqueBranches = useMemo(() => {
        return Array.from(new Set(rowData.map(r => r.branch).filter(Boolean))).sort();
    }, [rowData]);

    const filteredData = useMemo(() => {
        return rowData.filter(row => {
            if (selectedBranch && row.branch !== selectedBranch) return false;

            if (searchText) {
                const searchLower = searchText.toLowerCase();
                const codeMatch = row.staff_code?.toLowerCase().includes(searchLower);
                const nameMatch = row.full_name?.toLowerCase().includes(searchLower);
                if (!codeMatch && !nameMatch) return false;
            }
            return true;
        });
    }, [rowData, selectedBranch, searchText]);

    const exportToExcel = async () => {
        if (!filteredData || filteredData.length === 0) {
            alert("No data to export");
            return;
        }

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Leave Balances');

        // Prepare columns map
        const columns = [];
        columnDefs.forEach(col => {
            if (col.children) {
                col.children.forEach(child => {
                    columns.push({
                        header: `${col.headerName} - ${child.headerName}`,
                        key: child.field,
                        width: Math.max(10, Math.floor((child.width || 80) / 7))
                    });
                });
            } else if (col.headerName === "No.") {
                columns.push({
                    header: col.headerName,
                    key: "row_no",
                    width: Math.max(8, Math.floor((col.width || 60) / 7))
                });
            } else if (col.field) {
                columns.push({
                    header: col.headerName,
                    key: col.field,
                    width: Math.max(10, Math.floor((col.width || 120) / 7))
                });
            }
        });

        worksheet.columns = columns;

        // Add Data
        filteredData.forEach((row, index) => {
            const rowData = {};
            columns.forEach(c => {
                if (c.key === "row_no") {
                    rowData[c.key] = index + 1;
                } else {
                    rowData[c.key] = row[c.key];
                }
            });
            worksheet.addRow(rowData);
        });

        // Style the Header Row
        const headerRow = worksheet.getRow(1);
        headerRow.height = 40; // More row height
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF3F4F6' } // Light gray background
            };
        });

        // Generate and Download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = `Leave_Balances_${asOfDate}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(link.href);
    };

    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8fafc" }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />
            <Box sx={{ p: 4, flex: 1, display: "flex", flexDirection: "column", maxWidth: "1600px", margin: "0 auto", width: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 800, color: "#1a237e", mb: 0.5 }}>
                            Leave Balances Overview
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Monitor leave entitlements, taken, and remaining balances
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            type="date"
                            label="As Of Date"
                            value={asOfDate}
                            onChange={(e) => setAsOfDate(e.target.value)}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 160, bgcolor: 'white' }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={exportToExcel}
                            sx={{ height: 40 }}
                        >
                            Export Excel
                        </Button>
                    </Stack>
                </Box>

                <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2, bgcolor: '#fbfbfb' }}>
                    <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            select
                            label="Branch/Department"
                            size="small"
                            value={selectedBranch}
                            onChange={(e) => setSelectedBranch(e.target.value)}
                            sx={{ minWidth: 200, bgcolor: 'white' }}
                        >
                            <MenuItem value="">All</MenuItem>
                            {uniqueBranches.map(b => (
                                <MenuItem key={b} value={b}>{b}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Search Staff"
                            size="small"
                            placeholder="Name or Code..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2, bgcolor: 'white' }
                            }}
                            sx={{ minWidth: 250 }}
                        />
                    </Stack>
                </Paper>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Paper className="ag-theme-alpine" sx={{ flex: 1, width: "100%", borderRadius: 2, overflow: "hidden" }}>
                        <AgGridReact
                            rowData={filteredData}
                            columnDefs={columnDefs}
                            defaultColDef={{ resizable: true, sortable: true, filter: true }}
                            rowSelection="multiple"
                            pagination={true}
                            paginationPageSize={20}
                            onCellClicked={(event) => {
                                if (event.colDef.field && event.colDef.field.endsWith("_balance")) {
                                    const typeCode = event.colDef.field.replace("_balance", "");
                                    const lt = leaveTypes.find(t => t.type_code === typeCode);
                                    if (lt) {
                                        setSelectedAudit({
                                            staff_id: event.data.id,
                                            leave_type_id: lt.id,
                                            leave_type_name: lt.type_name,
                                            year: new Date(asOfDate).getFullYear()
                                        });
                                        setOpenAuditDialog(true);
                                    }
                                }
                            }}
                        />
                    </Paper>
                )}
            </Box>

            {/* Accrual Audit Dialog */}
            <Dialog
                open={openAuditDialog}
                onClose={() => setOpenAuditDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', px: 3, py: 2 }}>
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                        Accrual & Entitlement Details
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        {auditData?.full_name} • {auditData?.leave_type_name} • Year {selectedAudit?.year}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ mt: 2, px: 3 }}>
                    {auditLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : auditData ? (
                        <Box>
                            {auditData.apply_20_day_rule ? (
                                <Box>
                                    <Alert severity="info" sx={{ mb: 3 }}>
                                        Myanmar Labor Law 20-Day Rule applies: Employees must have <strong>at least 20 working days</strong> (Worked Days + Public Holidays + Paid Leaves) in a month, or <strong>1 day is deducted</strong> from their entitlement.
                                    </Alert>
                                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                                        <Table size="small">
                                            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Period</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Worked Days</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Holidays</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Paid Leaves</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Total Valid Days</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {auditData.breakdown.map((row, idx) => (
                                                    <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                        <TableCell>{row.period}</TableCell>
                                                        <TableCell align="right">{row.worked_days}</TableCell>
                                                        <TableCell align="right">{row.holidays}</TableCell>
                                                        <TableCell align="right">{row.paid_leaves}</TableCell>
                                                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{row.total_valid_days}</TableCell>
                                                        <TableCell align="center">
                                                            {row.is_penalty_applied ? (
                                                                <Chip label="-1 Day Penalty" color="error" size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
                                                            ) : (
                                                                <Chip label="Compliant" color="success" size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                    <Divider sx={{ my: 3 }} />
                                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
                                            Formula Summary:
                                        </Typography>
                                        <Stack direction="row" spacing={3} alignItems="center">
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Base Entitlement</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{auditData.base_entitlement} Days</Typography>
                                            </Box>
                                            <Typography variant="h5" sx={{ color: '#94a3b8' }}>-</Typography>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Total Penalty</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#ef4444' }}>{auditData.total_penalty} Days</Typography>
                                            </Box>
                                            <Typography variant="h5" sx={{ color: '#94a3b8' }}>=</Typography>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">Final Entitlement</Typography>
                                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#10b981' }}>{auditData.final_entitlement} Days</Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Box>
                            ) : (
                                <Box sx={{ py: 3, textAlign: 'center' }}>
                                    <Alert severity="warning" sx={{ display: 'inline-flex', textAlign: 'left' }}>
                                        This leave type ({auditData.leave_type_name}) does not use the 20-Day working rule calculation. The full base entitlement of <strong>{auditData.base_entitlement} Days</strong> is granted upfront.
                                    </Alert>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography color="error">Failed to load audit data.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOpenAuditDialog(false)} variant="contained" sx={{ bgcolor: '#1a237e' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeaveBalances;

